<script setup lang="ts">
import { computed, onMounted } from 'vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import type { CommunityDto, ContextDto } from '@/services/types/api.types';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import CommunityEditDialog from '@/components/CommunityEditDialog.vue';
import CommunityDuplicateDialog from '@/components/CommunityDuplicateDialog.vue';
import { useCommunityStore } from '@/stores/community.store';
import { Constants } from '@/constants';
import { getTranslation } from '@/composables/data-display-utils';

const $q = useQuasar();
const { t } = useI18n();
const communityStore = useCommunityStore();
const communities = computed(() => communityStore.communities);
const loading = computed(() => communityStore.isLoading);
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: any) => getTranslation(row, 'title', row.language_enabled),
    align: 'left',
  },
  {
    name: 'slug',
    label: t('community.slug'),
    field: 'slug',
    align: 'left',
  },
  {
    name: 'activeConsultation',
    label: t('community.activeConsultation'),
    field: 'active_consultations_count',
    align: 'left',
  },
]);

// Delete is appended by ReorderableTable itself. Duplicating hits a
// superadmin-only endpoint, and this view has no route guard of its own.
const actionMenuItems = computed(() => [
  { action: 'duplicate-community', label: t('community.duplicate'), requiresSuperadmin: true },
]);

// The shape ReorderableTable forwards for the items in actionMenuItems.
type CommunityTableActionEvent = { action: string; payload: { row: CommunityDto } };

const handleActions = (event: CommunityTableActionEvent) => {
  if (event.action !== 'duplicate-community') return;
  $q.dialog({
    component: CommunityDuplicateDialog,
    componentProps: { community: event.payload.row },
  });
};

const handleDeleteRow = (row: ContextDto) => {
  $q.dialog({
    title: 'Supprimer la communauté',
    message: 'Êtes-vous sûr de vouloir supprimer cette communauté ?',
    cancel: true,
    persistent: true,
  }).onOk(async () => {
    await communityStore.deleteCommunity(row);
  });
};
const handleRowClick = async (row: any) => {
  await communityStore.switchCommunity({ id: row.id }, true);
};
const handleAddRow = () => {
  const community: CommunityDto = communityStore.createCommunity({
    defaultLanguage: Constants.DefaultLocale,
    languageEnabled: [Constants.DefaultLocale],
  });
  $q.dialog({
    component: CommunityEditDialog,
    componentProps: {
      mode: 'add',
      community,
    },
  });
};

onMounted(async () => {
  await communityStore.fetchCommunities(false, true);
});
</script>

<template>
  <q-page class="community-list">
    <h1 class="title">{{ t('community.communities') }}</h1>
    <reorderable-table
      :rows="communities"
      :columns="columns"
      :loading="loading"
      itemKey="id"
      hideBottom
      :disableOrderable="true"
      :action-menu-items="actionMenuItems"
      @add-row="handleAddRow"
      @actions="handleActions"
      @delete-row="handleDeleteRow"
      @row-click="handleRowClick"
    >
      <template v-slot:body-cell-slug="props">
        <q-td :props="props">
          <a :href="communityStore.previewURL(props.row.id)" target="_blank" @click.stop
            >{{ props.row.slug }}
            <q-icon name="open_in_new" />
          </a>
        </q-td>
      </template>
    </reorderable-table>
  </q-page>
</template>

<style lang="scss">
.community-list {
  padding: 16px;

  .title {
    margin: 0 10px !important;
  }

  a {
    color: $primary;
    text-decoration: none;
  }
}
</style>
